import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import authService from '../services/authService';

export interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  clientId?: string; // For client-based tenants
}

export interface TenantContextType {
  tenant: Tenant | null;
  setTenant: (tenant: Tenant | null) => void;
  currentTenantId: string | null;
  setCurrentTenantId: (id: string | null) => void;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Helper to validate whether a string is a valid standard UUID
export const isUuid = (val: string | null | undefined): boolean => {
  if (!val || typeof val !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim());
};

// Mock tenant names for known IDs
const mockTenantNames: Record<string, string> = {
  "34c13c76-204d-43d5-90e3-56a50cfc4e84": "Clover Home",
  "383d7ced-61dd-4a20-88cf-7316bd6b6d66": "Lakewood Villa",
};

const getLogoForTenantId = (_id: string, _name: string): string => {
  return "/assets/JSNU-logo.png";
};

/**
 * Resolves the initial facility UUID with highest priority given to JWT claims,
 * avoiding invalid fallbacks like URL subdomain strings (e.g. 'carex').
 */
export const resolveInitialFacilityId = (): string | null => {
  // 1. Saved currentTenantId in localStorage (ONLY if valid UUID - preserves active facility selection)
  const savedTenantId = localStorage.getItem('currentTenantId');
  if (savedTenantId && isUuid(savedTenantId)) {
    return savedTenantId;
  }

  // 2. Read facility UUID from JWT token claims
  try {
    const token = localStorage.getItem('authToken');
    if (token) {
      const fromToken = authService.getFacilityIdFromToken(token);
      if (fromToken && isUuid(fromToken)) {
        return fromToken;
      }
    }
  } catch (err) {
    console.warn('Failed to extract facility ID from token on initialization:', err);
  }

  // 3. Fallback: Logged-in user data in localStorage
  try {
    const loggedInUserStr = localStorage.getItem('user');
    const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
    const userFacility = Array.isArray(loggedInUser?.facility) 
      ? (loggedInUser.facility.find((f: any) => f.isPrimary === true) || loggedInUser.facility[0]) 
      : (loggedInUser?.facility || null);
    const userFacilityId = userFacility?.id || loggedInUser?.facilityId || loggedInUser?.tenantId;
    if (userFacilityId && isUuid(userFacilityId)) {
      return String(userFacilityId);
    }
  } catch {}

  return null;
};

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);

  // Initialize currentTenantId synchronously on first render from token claims
  const [currentTenantId, setCurrentTenantIdState] = useState<string | null>(() => resolveInitialFacilityId());

  const setCurrentTenantId = useCallback((id: string | null) => {
    if (id && isUuid(id)) {
      setCurrentTenantIdState(id);
      localStorage.setItem('currentTenantId', id);
    } else if (!id) {
      setCurrentTenantIdState(null);
      localStorage.removeItem('currentTenantId');
    } else {
      console.warn(`[TenantContext] Blocked non-UUID facility ID "${id}". Falling back to JWT facility.`);
      const fallback = resolveInitialFacilityId();
      if (fallback) {
        setCurrentTenantIdState(fallback);
        localStorage.setItem('currentTenantId', fallback);
      }
    }
  }, []);

  // Ensure facility UUID and tenant object are rehydrated on initial mount / reload
  useEffect(() => {
    const facilityId = resolveInitialFacilityId();
    if (facilityId) {
      setCurrentTenantIdState(facilityId);
      localStorage.setItem('currentTenantId', facilityId);

      try {
        const loggedInUserStr = localStorage.getItem('user');
        const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
        const userFacility = Array.isArray(loggedInUser?.facility) 
          ? (loggedInUser.facility.find((f: any) => f.isPrimary === true) || loggedInUser.facility[0]) 
          : (loggedInUser?.facility || null);

        const tenantName = userFacility?.name || mockTenantNames[facilityId] || "CareX Facility";
        setTenant({
          id: facilityId,
          name: tenantName,
          logoUrl: userFacility?.logoUrl || getLogoForTenantId(facilityId, tenantName),
          clientId: 'client-1',
        });
      } catch (err) {
        console.error('Failed to rehydrate tenant details:', err);
      }
    }
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, setTenant, currentTenantId, setCurrentTenantId }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
